import { JSX } from "react";
import { ComponentTypes } from "../componentTypes";
import type {
  All3PageContent,
  All4PageContent,
  All5PageContent,
  All7PageContent,
  All8PageContent,
  AllPageContent,
  Extended2PageContent,
  MarginContent,
  MarginComponent,
} from "../types";
import type { RendererConfig } from "./rendererConfig";
import { COMPONENT_REGISTRY } from "./componentRegistry";


type RendererComponents =
  | AllPageContent
  | Extended2PageContent
  | All3PageContent
  | All4PageContent
  | All5PageContent
  | All7PageContent
  | All8PageContent;

/** Page renderer component props. */
export interface PageRendererProps {
  components: RendererComponents;
  config?: RendererConfig;
  /**
   * Extra className appended to the FIRST component's wrapper div. Used by
   * &lt;PageShell&gt; to apply layout-driven classes (e.g., mobile bleed) without
   * the renderer needing to know about layout semantics.
   */
  firstWidgetClassName?: string;
  /** Extra className appended to the LAST component's wrapper div. */
  lastWidgetClassName?: string;
}

const DEFAULT_MARGIN_CLASSES: Required<MarginContent> = {
  mobile: "mt-8",
  md: "md:mt-10",
  lg: "lg:mt-12",
};

const buildMarginClassName = (content?: MarginContent | null) => {
  const mobile = content?.mobile?.trim() || DEFAULT_MARGIN_CLASSES.mobile;
  const md = content?.md?.trim() || DEFAULT_MARGIN_CLASSES.md;
  const lg = content?.lg?.trim() || DEFAULT_MARGIN_CLASSES.lg;
  return [mobile, md, lg].filter(Boolean).join(" ").trim();
};

/** Main page renderer: orchestrates sequential display of all components. */
export function PageRenderer({ components, config, firstWidgetClassName, lastWidgetClassName }: PageRendererProps) {
  const lastIndex = components.length - 1;
  return (
    <div>
      {components.map((component, index) => {
        const prevComponentType = index > 0 ? components[index - 1]?.componentType : null;
        const prevIsMargin = prevComponentType === ComponentTypes.Margin;
        // Vertical rhythm — THREE steps, owned here (not by the blocks):
        //   FLUSH   "mt-0"            full-bleed bands / blocks that self-space
        //   RELATED "mt-6 md:mt-8"    content that continues the current section
        //   SECTION "mt-12 md:mt-16"  blocks that OPEN a new section
        // TextBlock/RichText keep a tighter mt-4 (intra-prose sub-step).
        const FLUSH = "mt-0";
        const RELATED = "mt-5 md:mt-6";
        const SECTION = "mt-10 md:mt-12";
        let topMarginClass = index === 0 ? "" : RELATED;
        if (prevIsMargin) {
          topMarginClass = "mt-0";
        }
        const layoutClassesByType: Partial<Record<string, string>> = {
          [ComponentTypes.PageHeader]: "w-full col-span-full", // header takes full horizontal space
        };
        const spacingClassesByType: Partial<Record<string, string>> = {
          // FLUSH — full-bleed bands and blocks that own their own rhythm.
          [ComponentTypes.ArticleHero]: FLUSH,
          [ComponentTypes.SingleLink]: FLUSH,
          [ComponentTypes.Hero]: FLUSH,
          [ComponentTypes.NewsSection]: FLUSH,
          [ComponentTypes.StatsSummary]: FLUSH,
          [ComponentTypes.AudienceTools]: FLUSH,
          [ComponentTypes.SearchBar]: FLUSH,
          [ComponentTypes.PublicationsUpdates]: FLUSH,
          [ComponentTypes.HomeHero]: "",
          [ComponentTypes.HomeNews]: FLUSH,
          [ComponentTypes.HomeBlueStats]: FLUSH,
          [ComponentTypes.HomeBlueTools]: FLUSH,
          [ComponentTypes.HomePubUpdatesSection]: FLUSH,
          [ComponentTypes.NewsletterSignup]: FLUSH,
          [ComponentTypes.KeyIndicatorsBar]: FLUSH,
          [ComponentTypes.InformationHub]: FLUSH,
          [ComponentTypes.MarketsBoard]: FLUSH,
          // The finder is a full-bleed gray band that butts flush under the
          // dark hero on home; the inline variant self-spaces (its own mt-8).
          [ComponentTypes.PartsFinder]: FLUSH,
          [ComponentTypes.SpecialtyCards]: FLUSH,
          [ComponentTypes.StepsSection]: SECTION,
          [ComponentTypes.TrustBar]: RELATED,
          [ComponentTypes.BrandStrip]: FLUSH,
          // CtaBanner's primary variant self-spaces (mt-12/16 inside); soft flows.
          [ComponentTypes.CtaBanner]: RELATED,

          // SECTION openers.
          [ComponentTypes.SectionHeading]: SECTION,
          [ComponentTypes.LinkTileGrid]: SECTION,
          [ComponentTypes.SplitImageCallout]: SECTION,
          [ComponentTypes.BlueCallout]: SECTION,

          // RELATED content.
          [ComponentTypes.ActivityGrid]: RELATED,
          [ComponentTypes.PaginatedActivityGrid]: RELATED,
          [ComponentTypes.PersonCardGrid]: RELATED,
          [ComponentTypes.EventGrid]: RELATED,
          [ComponentTypes.ArticleGrid]: RELATED,
          [ComponentTypes.LatestArticleGrid]: RELATED,
          [ComponentTypes.Callout]: RELATED,
          [ComponentTypes.StatsGrid]: RELATED,
          [ComponentTypes.TextCardGrid]: RELATED,
          [ComponentTypes.LabeledTextGrid]: RELATED,
          [ComponentTypes.CompanyReportGrid]: RELATED,
          [ComponentTypes.BlogArticleGrid]: RELATED,
          [ComponentTypes.Gallery]: RELATED,
          [ComponentTypes.BannerImage]: RELATED,
          [ComponentTypes.FullWidthImage]: RELATED,
          [ComponentTypes.IconCardGrid]: RELATED,
          [ComponentTypes.TextWithSideImage]: RELATED,
          [ComponentTypes.TextWithSidePanel]: RELATED,
          [ComponentTypes.LegislationList]: RELATED,
          [ComponentTypes.NumberedList]: RELATED,
          [ComponentTypes.TwoColumnList]: RELATED,
          [ComponentTypes.TextWithFigure]: RELATED,
          [ComponentTypes.BilletTabs]: RELATED,
          [ComponentTypes.ContactForm]: RELATED,
          [ComponentTypes.SeriesExplorer]: RELATED,
          [ComponentTypes.KeyMetricsExplorer]: RELATED,
          [ComponentTypes.FxRatesExplorer]: RELATED,
          [ComponentTypes.NewsList]: RELATED,
          [ComponentTypes.AccordionSection]: RELATED,
          [ComponentTypes.Coordonnees]: RELATED,
          [ComponentTypes.DynamicComponents]: RELATED,
          [ComponentTypes.DataRecordDetail]: RELATED,
          [ComponentTypes.VideoEmbed]: RELATED,
          [ComponentTypes.ProductBuyBox]: RELATED,

          // Intra-prose sub-step.
          [ComponentTypes.TextBlock]: "mt-4",
          [ComponentTypes.RichText]: "mt-4",
        };
        const layoutClass = layoutClassesByType[component.componentType];
        const spacingClass = spacingClassesByType[component.componentType];
        if (spacingClass && !prevIsMargin && component.componentType !== ComponentTypes.Margin) {
          topMarginClass = spacingClass;
        }
        if (component.componentType === ComponentTypes.Margin) {
          topMarginClass = buildMarginClassName((component as MarginComponent).content);
        }

        // Dispatch via the component registry (1:1 replacement for the former 60-case switch).
        // A missing entry renders nothing — the registry's exhaustiveness is unit-tested.
        const renderer = COMPONENT_REGISTRY[component.componentType];
        const content: JSX.Element | null = renderer ? renderer(component, config) : null;

        const positionClass =
          (index === 0 ? firstWidgetClassName : "") ||
          (index === lastIndex ? lastWidgetClassName : "") ||
          "";
        const wrapperClass = [topMarginClass, layoutClass, positionClass].filter(Boolean).join(" ");
        return (
          <div key={component.key ?? `cmp-${index}`} className={wrapperClass}>
            {content}
          </div>
        );
      })}
    </div>
  );
}
